import PropTypes from 'prop-types';
import React, { useState } from 'react';
import s from './WorkTracker.module.scss';
import { eventShape, taskShape } from './propTypes';
import {
  MONTH_NAMES, monthGrid, railTone, toISODate,
} from './lib/dates';

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

// A tiny inline box inside a day cell: add an event, or rename/delete one.
function DayBox({
  initial, onSave, onDelete, onTaskInstead, onClose,
}) {
  const [name, setName] = useState(initial || '');
  const submit = () => { const n = name.trim(); if (n) onSave(n); };
  const onKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); submit(); }
    if (e.key === 'Escape') { e.stopPropagation(); onClose(); }
  };
  return (
    <div className={s.dayBox} onClick={(e) => e.stopPropagation()} role="presentation">
      <input
        className={s.input}
        value={name}
        placeholder="Event name"
        aria-label="Event name"
        autoFocus // eslint-disable-line jsx-a11y/no-autofocus
        onChange={(e) => setName(e.target.value)}
        onKeyDown={onKeyDown}
      />
      <div className={s.dayBoxRow}>
        <button type="button" className={`${s.btnPrimary} ${s.btnSm}`} onClick={submit}>
          {initial ? 'Save' : 'Add event'}
        </button>
        {onDelete && (
          <button type="button" className={`${s.btnDanger} ${s.btnSm}`} onClick={onDelete}>Delete</button>
        )}
        {onTaskInstead && (
          <button type="button" className={`${s.btn} ${s.btnSm}`} onClick={() => onTaskInstead(name.trim())}>
            Task instead
          </button>
        )}
        <button type="button" className={`${s.btnQuiet} ${s.btnSm}`} onClick={onClose}>Cancel</button>
      </div>
    </div>
  );
}

DayBox.propTypes = {
  initial: PropTypes.string,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  onTaskInstead: PropTypes.func,
  onClose: PropTypes.func.isRequired,
};
DayBox.defaultProps = { initial: '', onDelete: undefined, onTaskInstead: undefined };

export default function CalendarView({
  tasks, events, today, month, onMonth, onOpenTask,
  onAddEvent, onRenameEvent, onDeleteEvent, onTaskInstead, undatedCount, onShowList,
}) {
  const [adding, setAdding] = useState(null); // iso date
  const [editing, setEditing] = useState(null); // event
  const days = monthGrid(month);
  const todayIso = toISODate(today);
  const tasksByDay = {};
  tasks.forEach((t) => { if (t.due_on) (tasksByDay[t.due_on] ||= []).push(t); });
  const eventsByDay = {};
  events.forEach((e) => { (eventsByDay[e.on_date] ||= []).push(e); });
  const closeBoxes = () => { setAdding(null); setEditing(null); };
  const shift = (n) => onMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const goToday = () => onMonth(new Date(today.getFullYear(), today.getMonth(), 1));

  return (
    <div className={s.calendar}>
      <div className={s.calHead}>
        <h2 className={s.calMonth}>
          {MONTH_NAMES[month.getMonth()]}
          {' '}
          {month.getFullYear()}
        </h2>
        <div className={s.calNav}>
          <button type="button" className={`${s.btn} ${s.btnSm}`} onClick={() => shift(-1)} aria-label="Previous month">
            ←
          </button>
          <button type="button" className={`${s.btn} ${s.btnSm}`} onClick={goToday}>Today</button>
          <button type="button" className={`${s.btn} ${s.btnSm}`} onClick={() => shift(1)} aria-label="Next month">
            →
          </button>
        </div>
      </div>
      <div className={s.weekdays}>{WEEKDAYS.map((d) => <div key={d}>{d}</div>)}</div>
      <div className={s.grid}>
        {days.map((day) => {
          const iso = toISODate(day);
          return (
            <div
              key={iso}
              className={s.day}
              data-outside={day.getMonth() !== month.getMonth() ? 'true' : undefined}
              onClick={() => { setEditing(null); setAdding(iso); }}
              role="presentation"
            >
              <span className={s.dayNum} data-today={iso === todayIso ? 'true' : undefined}>{day.getDate()}</span>
              <div className={s.pills}>
                {(eventsByDay[iso] || []).map((ev) => (
                  <button
                    type="button"
                    key={`e${ev.id}`}
                    className={s.eventPill}
                    title={ev.name}
                    onClick={(e) => { e.stopPropagation(); setAdding(null); setEditing(ev); }}
                  >
                    {ev.name}
                  </button>
                ))}
                {(tasksByDay[iso] || []).map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    className={s.taskPill}
                    data-rail={railTone(t, today)}
                    title={t.title}
                    onClick={(e) => { e.stopPropagation(); onOpenTask(t); }}
                  >
                    {t.title}
                  </button>
                ))}
              </div>
              {adding === iso && (
                <DayBox
                  onSave={async (name) => { if (await onAddEvent({ name, on_date: iso })) closeBoxes(); }}
                  onTaskInstead={(title) => { closeBoxes(); onTaskInstead({ title, due_on: iso }); }}
                  onClose={closeBoxes}
                />
              )}
              {editing && editing.on_date === iso && (
                <DayBox
                  key={editing.id}
                  initial={editing.name}
                  onSave={async (name) => { if (await onRenameEvent(editing, name)) closeBoxes(); }}
                  onDelete={() => { closeBoxes(); onDeleteEvent(editing); }}
                  onClose={closeBoxes}
                />
              )}
            </div>
          );
        })}
      </div>
      <div className={s.calFoot}>
        {undatedCount === 0 ? 'Every task has a due date.' : (
          <>
            {undatedCount}
            {' '}
            task
            {undatedCount === 1 ? ' has' : 's have'}
            {' '}
            no due date and
            {undatedCount === 1 ? ' is' : ' are'}
            {' '}
            only on the
            {' '}
            <button type="button" className={s.linkBtn} onClick={onShowList}>list</button>
            .
          </>
        )}
      </div>
    </div>
  );
}

CalendarView.propTypes = {
  tasks: PropTypes.arrayOf(taskShape).isRequired,
  events: PropTypes.arrayOf(eventShape).isRequired,
  today: PropTypes.instanceOf(Date).isRequired,
  month: PropTypes.instanceOf(Date).isRequired,
  onMonth: PropTypes.func.isRequired,
  onOpenTask: PropTypes.func.isRequired,
  onAddEvent: PropTypes.func.isRequired,
  onRenameEvent: PropTypes.func.isRequired,
  onDeleteEvent: PropTypes.func.isRequired,
  onTaskInstead: PropTypes.func.isRequired,
  undatedCount: PropTypes.number.isRequired,
  onShowList: PropTypes.func.isRequired,
};
